import { toast } from "@shared/ui/use-toast";
import {
  camelToSnakeCaseInterceptor,
  snakeToCamelCaseInterceptor,
} from "@shared/utils/caseConverter";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import { getAuthToken } from "../../auth/authUtils";
import { API_BASE_URLS, ApiType } from "../api-endpoints";
import { queryClient } from "../queries/queryClient";

// Create a custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public data?: any
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiClientConfig {
  timeout?: number;
  retries?: number;
  offlineSupport?: boolean;
  requiresAuth?: boolean;
  customHeaders?: Record<string, string>;
}

const SERVICE_CONFIGS: Record<ApiType, ApiClientConfig> = {
  auth: {
    timeout: 30000,
    retries: 1,
    requiresAuth: true,
  },
  cx: {
    timeout: 60000,
    retries: 3,
    offlineSupport: true,
    requiresAuth: true,
  },
  onboarding: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  dataMigration: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  bx: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  ax: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  mx: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  wx: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  rx: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
  communication: {
    timeout: 40000,
    retries: 5,
    requiresAuth: true,
  },
  activityLog: {
    timeout: 30000,
    retries: 1,
    requiresAuth: true,
  },
  star: {
    timeout: 30000,
    retries: 2,
    requiresAuth: true,
  },
  consumerWeb: undefined,
  receipt: {
    timeout: 30000,
    retries: 2,
    requiresAuth: true,
  },
  consumerCommunication: {
    timeout: 30000,
    retries: 2,
    requiresAuth: true,
  },
  payment: {
    timeout: 45000,
    retries: 2,
    requiresAuth: true,
  },
};

// Optimized request deduplication with automatic cleanup
const pendingRequests = new Map<string, Promise<any>>();
const requestTimestamps = new Map<string, number>();
const REQUEST_CACHE_TTL = 30000; // 30 seconds

const createRequestKey = (method: string, url: string, data?: any): string => {
  return `${method}:${url}:${JSON.stringify(data || {})}`;
};

// Cleanup expired pending requests for better performance
const cleanupExpiredRequests = () => {
  const now = Date.now();
  for (const [key, timestamp] of requestTimestamps.entries()) {
    if (now - timestamp > REQUEST_CACHE_TTL) {
      pendingRequests.delete(key);
      requestTimestamps.delete(key);
    }
  }
};

// Service-specific error handler
const handleServiceError = (
  serviceType: ApiType,
  status: number,
  data?: any,
  url?: string
) => {
  const servicePrefix = `[${serviceType.toUpperCase()}]`;
  const nonAuthUrls = ["/login", "/forgot-password", "/reset-password", "/"];

  switch (status) {
    case 400:
      toast({
        title: "Error",
        description: `${servicePrefix} Bad request. Please check your input.`,
        variant: "destructive",
      });
      break;

    case 401:
      if (
        serviceType !== "auth" ||
        (url &&
          !nonAuthUrls.some(
            (authUrl) => url === authUrl || url.endsWith(authUrl)
          ))
      ) {
        toast({
          title: "Error",
          description: "Session expired. Please login again.",
          variant: "destructive",
        });
        localStorage.clear();
        sessionStorage.clear();
        queryClient.clear();
        if (window.caches) {
          caches.keys().then((cacheNames) => {
            cacheNames.forEach((cacheName) => {
              caches.delete(cacheName);
            });
          });
        }
        window.location.href = "/login";
      }
      break;

    case 403:
      toast({
        title: "Error",
        description: `${servicePrefix} You do not have permission to perform this action.`,
        variant: "destructive",
      });
      break;

    case 404:
      toast({
        title: "Error",
        description: `${servicePrefix} The requested resource was not found.`,
        variant: "destructive",
      });
      break;

    case 422:
      toast({
        title: "Error",
        description: `${servicePrefix} ${
          data?.message || "Validation error. Please check your input."
        }`,
        variant: "destructive",
      });
      break;

    case 429:
      toast({
        title: "Error",
        description: `${servicePrefix} Too many requests. Please try again later.`,
        variant: "destructive",
      });
      break;

    case 500:
      toast({
        title: "Error",
        description:
          serviceType === "cx"
            ? "CX service temporarily unavailable. Working offline."
            : `${servicePrefix} An internal server error occurred. Please try again later.`,
        variant: "destructive",
      });
      break;

    case 502:
      toast({
        title: "Error",
        description: `${servicePrefix} Bad gateway. Please try again later.`,
        variant: "destructive",
      });
      break;

    case 503:
      toast({
        title: "Error",
        description: `${servicePrefix} Service unavailable. Please try again later.`,
        variant: "destructive",
      });
      break;

    case 504:
      toast({
        title: "Error",
        description: `${servicePrefix} Gateway timeout. Please try again later.`,
        variant: "destructive",
      });
      break;

    default:
      toast({
        title: "Error",
        description:
          data?.message || `${servicePrefix} An unexpected error occurred`,
        variant: "destructive",
      });
  }
};

const createServiceInterceptors = (
  serviceType: ApiType,
  config: ApiClientConfig
) => {
  const nonAuthUrls = ["/login", "/forgot-password", "/reset-password"];
  const retryCount = new Map<string, number>();

  return {
    request: async (requestConfig: InternalAxiosRequestConfig) => {
      console.log(
        `[${serviceType.toUpperCase()}] Making request to:`,
        requestConfig.url
      );

      // Cleanup expired requests periodically
      if (Math.random() < 0.1) {
        // 10% chance to cleanup on each request
        cleanupExpiredRequests();
      }

      // Check for duplicate requests
      const requestKey = createRequestKey(
        requestConfig.method || "GET",
        requestConfig.url || "",
        requestConfig.data
      );

      // If there's a pending request with the same key, return its promise
      if (pendingRequests.has(requestKey)) {
        console.log(
          `[${serviceType.toUpperCase()}] Duplicate request detected, reusing existing request`
        );

        const pendingPromise = pendingRequests.get(requestKey);

        if (pendingPromise) {
          try {
            return await pendingPromise;
          } catch (error) {
            // If the pending request failed, allow this one to proceed
            pendingRequests.delete(requestKey);
            requestTimestamps.delete(requestKey);
          }
        }
      }

      // Add auth token for protected endpoints
      const url = requestConfig.url || "";
      const isPublicAuthUrl = nonAuthUrls.some(
        (authUrl) => url === authUrl || url.endsWith(authUrl)
      );

      console.log(
        "isPublicAuthUrl",
        isPublicAuthUrl,
        "url",
        url,
        "requiresAuth",
        config.requiresAuth
      );

      // Add token for all endpoints that require authentication
      if (config.requiresAuth && !isPublicAuthUrl) {
        const token = getAuthToken();
        console.log(
          "Token retrieved:",
          token ? "Token found" : "No token found"
        );

        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
          console.log(
            "Authorization header added:",
            requestConfig.headers.Authorization
          );
        } else {
          console.warn("No auth token available for protected endpoint:", url);
        }
      }

      // FIXED: Check if the request data is FormData OR if responseType is blob
      const isFormData = requestConfig.data instanceof FormData;
      const isBlobResponse = requestConfig.responseType === "blob";

      // FIXED: Only set Content-Type for non-FormData requests and non-blob responses
      // When sending FormData or expecting blob, let the browser/axios handle Content-Type
      if (!isFormData && !isBlobResponse) {
        requestConfig.headers["Content-Type"] = "application/json";
      }
      // For FormData or blob responses, don't set Content-Type - let axios handle it automatically

      // Add custom headers if needed
      if (config.customHeaders) {
        Object.assign(requestConfig.headers, config.customHeaders);
      }

      // Check if the URL contains any dropdown-related keywords
      const isDropdownRequest = requestConfig.url
        ?.toLowerCase()
        .match(/(isdropdown)/);

      // Dispatch loading start event with method and URL
      if (requestConfig.method) {
        window.dispatchEvent(
          new CustomEvent("", {
            detail: {
              method: requestConfig.method.toUpperCase(),
              url: isDropdownRequest ? requestConfig.url : undefined,
            },
          })
        );
      }

      // CX offline support
      if (serviceType === "cx" && config.offlineSupport) {
        if (!navigator.onLine) {
          console.log("[CX] Offline detected, queueing request");
          // Implementation would go here
        }
      }

      // Track this request
      requestTimestamps.set(requestKey, Date.now());

      // FIXED: Only apply camelToSnakeCaseInterceptor for non-FormData requests
      if (isFormData) {
        console.log(
          "[FormData] Skipping camelToSnakeCase conversion for FormData"
        );
        return requestConfig;
      } else {
        const skipSnakeCaseUrls = [
          "/onboarding/plan",
          "/meter-reading-format",
          "/template-configuration",
          "/consumer_status",
          "/INSTALLATION_COMPLETED",
          "/DOCUMENT_TYPE",
          "/nsc-connection-configuration/",
          "/ADDRESS_DISPLAY",
          "/ax/assets/",
          "/cx/reconnect-activate-consumer/"
        ];

        const shouldSkipSnakeCase = skipSnakeCaseUrls.some((pattern) =>
          (requestConfig.url || "").includes(pattern)
        );
        if (shouldSkipSnakeCase) {
          console.log(
            "[Interceptor] Skipping camelToSnakeCaseInterceptor for:",
            requestConfig.url
          );
          return requestConfig;
        }

        return camelToSnakeCaseInterceptor(requestConfig);
      }
    },

    response: (response: AxiosResponse) => {
      console.log(`[${serviceType.toUpperCase()}] Response received:`, {
        status: response.status,
        url: response.config.url,
        contentType: response.headers["content-type"],
      });

      // Clear retry count and remove from pending requests
      const requestKey = createRequestKey(
        response.config.method || "GET",
        response.config.url || "",
        response.config.data
      );

      retryCount.delete(requestKey);
      pendingRequests.delete(requestKey);
      requestTimestamps.delete(requestKey);

      // Dispatch loading end event for successful responses
      window.dispatchEvent(new CustomEvent("api-loading-end"));

      // FIXED: Don't apply snake_case to camelCase conversion for blob responses
      const isBlobResponse = response.config.responseType === "blob";
      if (isBlobResponse) {
        console.log(
          "[Blob Response] Skipping snakeToCamelCase conversion for blob data"
        );
        return response;
      }

      // Apply snake_case to camelCase conversion only for JSON responses
      return snakeToCamelCaseInterceptor(response);
    },

    error: async (error: AxiosError) => {
      console.error(`[${serviceType.toUpperCase()}] API Error:`, {
        status: error.response?.status,
        url: error.config?.url,
        message: error.message,
      });

      // Handle retry logic for specific errors (avoid retrying auth failures)
      if (error.config && error.response?.status) {
        const requestKey = createRequestKey(
          error.config.method || "GET",
          error.config.url || "",
          error.config.data
        );

        const currentRetries = retryCount.get(requestKey) || 0;
        const maxRetries = config.retries || 1;
        const status = error.response.status;

        // Don't retry client errors (4xx) - these are usually auth or validation issues
        // Only retry server errors (5xx) and rate limiting (429)
        const shouldRetry =
          (status >= 500 || status === 429) && currentRetries < maxRetries;

        if (shouldRetry) {
          retryCount.set(requestKey, currentRetries + 1);
          console.log(
            `[${serviceType.toUpperCase()}] Retrying request (${
              currentRetries + 1
            }/${maxRetries}) for status ${status}`
          );

          // Add exponential backoff delay
          const delay = Math.min(1000 * Math.pow(2, currentRetries), 10000);
          await new Promise((resolve) => setTimeout(resolve, delay));

          return axios(error.config);
        }

        // Clean up retry count for failed requests that won't be retriedf
        retryCount.delete(requestKey);
      }

      // Remove the request from pending requests and cleanup
      if (error.config) {
        const requestKey = createRequestKey(
          error.config.method || "GET",
          error.config.url || "",
          error.config.data
        );

        pendingRequests.delete(requestKey);
        requestTimestamps.delete(requestKey);
        retryCount.delete(requestKey);
      }

      // Dispatch loading end event
      window.dispatchEvent(new CustomEvent("api-loading-end"));

      const data = error.response?.data as { message?: string; code?: string };
      const status = error.response?.status || 0;

      // Handle service-specific errors
      if (serviceType === "communication" && status >= 500) {
        console.warn("Notification service unavailable");
        // Don't show error toast for notification service failures
        return Promise.resolve({
          data: null,
          status: 200,
          statusText: "OK",
          headers: {},
          config: error.config,
        });
      } else {
        // handleServiceError(serviceType, status, data, error.config?.url);
      }

      // Create a standardized error object
      // const apiError = new ApiError(
      //   status,
      //   data?.message || error.message || "An unexpected error occurred",
      //   data
      // );

      // Create a standardized error object
      const apiError = new ApiError(
        status,
        typeof data?.message === "string"
          ? data.message
          : Array.isArray(data?.message) &&
            (data.message as unknown[]).length > 0
          ? (data.message as unknown[])[0]
          : typeof data?.message === "object" &&
            data?.message !== null &&
            "message" in data.message
          ? (data.message as any).message
          : error.message || "An unexpected error occurred",
        data
      );

      throw apiError;
    },
  };
};

export const createServiceApiClient = (serviceType: ApiType): AxiosInstance => {
  const config = SERVICE_CONFIGS[serviceType];
  const baseURL = API_BASE_URLS[serviceType];

  if (!baseURL) {
    throw new Error(`No base URL configured for service: ${serviceType}`);
  }
  const client = axios.create({
    baseURL,
    timeout: config.timeout || 30000,
    headers: {
      ...config.customHeaders,
    },
    validateStatus: (status) => status >= 200 && status < 300,
  });

  // Add service-specific interceptors
  const serviceInterceptors = createServiceInterceptors(serviceType, config);

  // Request interceptor
  client.interceptors.request.use(serviceInterceptors.request, (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  });

  // Response interceptor
  client.interceptors.response.use(
    serviceInterceptors.response,
    serviceInterceptors.error
  );

  // Add request tracking for deduplication
  const originalRequest = client.request;
  client.request = function (config) {
    const requestKey = createRequestKey(
      config.method || "GET",
      config.url || "",
      config.data
    );

    const requestPromise = originalRequest.call(this, config);
    pendingRequests.set(requestKey, requestPromise);

    // Auto-cleanup when request completes
    requestPromise.finally(() => {
      pendingRequests.delete(requestKey);
      requestTimestamps.delete(requestKey);
    });

    return requestPromise;
  };

  return client;
};

// Create a default API client for backward compatibility

export const defaultApiClient = createServiceApiClient("auth");
export const authApiClient = createServiceApiClient("auth");
export const cxApiClient = createServiceApiClient("cx");
export const onboardingApiClient = createServiceApiClient("onboarding");
export const dataMigrationApiClient = createServiceApiClient("dataMigration");
export const consumerCommunicationApiClient = createServiceApiClient(
  "consumerCommunication"
);
export const bxApiClient = createServiceApiClient("bx");
export const axApiClient = createServiceApiClient("ax");
export const mxApiClient = createServiceApiClient("mx");
export const wxApiClient = createServiceApiClient("wx");
export const rxApiClient = createServiceApiClient("rx");
export const activityLogApiClient = createServiceApiClient("activityLog");
export const starApiClient = createServiceApiClient("star");
export const communicationApiClient = createServiceApiClient("communication");
export const receiptApiClient = createServiceApiClient("receipt");
export const paymentApiClient = createServiceApiClient("payment");
